import {
  EnvelopeLight,
  SocialMediaFacebook1Light,
  SocialMediaTwitterBold,
  MessagingWhatsappBold,
} from '@ha/icons';
import React from 'react';
import { Location } from 'react-router-dom';

import { Link } from '@ha/dsl';
import { useIntl } from 'ha/i18n';
import { ShareUrls, getShareUrls } from 'ha/utils/socialShareURLs';
import { SmartTrack as Track } from 'ha/components/track/SmartTrack';

import { SvgIcon } from 'ha/components.legacy/SvgIcon';

interface Props {
  location: Location;
  children: ({
    shareUrls,
    handleFbMessengerClick,
  }: {
    shareUrls: ShareUrls;
    handleFbMessengerClick: () => void;
  }) => JSX.Element | null;
  facebookAppID?: string;
  shareMessage?: string;
  countOfConversations?: number;
  isDisabled?: boolean;
  isMobile?: boolean;
}

const ShareToSocialNetworks: React.FC<Props> = ({
  facebookAppID = '410300212483493',
  shareMessage = '',
  countOfConversations = 0,
  isDisabled,
  isMobile,
  location: { pathname },
  children,
}) => {
  const { T } = useIntl();
  const handleFbMessengerClick = React.useCallback(() => {
    if (!global.FB) {
      return;
    }

    global.FB.ui({
      method: 'send',
      app_id: facebookAppID,
      link: `https://housinganywhere.com${pathname}`,
    });
  }, [facebookAppID, pathname]);

  const shareUrl = React.useMemo(
    () => `https://housinganywhere.com${pathname}`,
    [pathname],
  );

  const urls = React.useMemo(
    () => getShareUrls(shareUrl, shareMessage, facebookAppID),
    [shareUrl, shareMessage, facebookAppID],
  );

  if (isDisabled) {
    return null;
  }

  if (typeof children === 'function') {
    return children({ handleFbMessengerClick, shareUrls: urls });
  }

  return (
    <div
      className="card border-outline text-left p-a-1"
      data-test-locator="ShareToSocialNetworks"
    >
      <div className="strong">{T('Share it:')}</div>

      <div className="card--row p-x-0 p-b-0">
        <Track name="Clicked a share button" type="twitter">
          <Link
            to={urls.twitter}
            target="_blank"
            rel="noopener noreferer nofollow"
            className="social-button social-button--twitter"
            data-test-locator="Share via Twitter"
            title={T('Share via Twitter')}
          >
            <SocialMediaTwitterBold fontSize="small" />
          </Link>
        </Track>
        <Track name="Clicked a share button" type="facebook">
          <Link
            to={urls.facebook}
            target="_blank"
            rel="noopener noreferer nofollow"
            className="social-button social-button--facebook"
            data-test-locator="Share via Facebook"
            title={T('Share via Facebook')}
          >
            <SocialMediaFacebook1Light fontSize="small" />
          </Link>
        </Track>

        <Track name="Clicked a share button" type="email">
          <Link
            to={urls.email}
            target="_self"
            rel="noopener noreferer nofollow"
            className="social-button"
            data-test-locator="Share via E-Mail"
            title={T('Share via E-Mail')}
          >
            <EnvelopeLight fontSize="small" />
          </Link>
        </Track>

        {isMobile && (
          <Track name="Clicked a share button" type="whatsapp">
            <Link
              to={urls.whatsapp}
              className="social-button social-button--whatsapp"
              data-action="share/whatsapp/share"
              data-test-locator="Share via Whatsapp"
              title={T('Share via Whatsapp')}
              rel="noopener noreferer nofollow"
            >
              <MessagingWhatsappBold fontSize="small" />
            </Link>
          </Track>
        )}

        {isMobile && (
          <Track name="Clicked a share button" type="facebook-messager">
            <Link
              to={urls.messenger}
              className="social-button"
              data-test-locator="Share via Facebook Messenger"
              title={T('Share via Facebook Messenger')}
              rel="noopener noreferer nofollow"
            >
              <SvgIcon className="icon-1_5" name="facebook-messenger" />
            </Link>
          </Track>
        )}

        {!isMobile && (
          <Track name="Clicked a share button" type="facebook-messager">
            <Link
              to=""
              onClick={handleFbMessengerClick}
              className="social-button"
              type="button"
              data-test-locator="Share via Facebook Messenger"
              title={T('Share via Facebook Messenger')}
              rel="noopener noreferer nofollow"
            >
              <SvgIcon className="icon-1_5" name="facebook-messenger" />
            </Link>
          </Track>
        )}
      </div>

      {countOfConversations ? (
        <div className="small text-center color-base-light m-t-0_5">
          {T('%d people are eyeing this place', countOfConversations)}
        </div>
      ) : null}
    </div>
  );
};

export { ShareToSocialNetworks };
